/**
 * Centralized file size limits for WhatsApp/WPPConnect media uploads.
 * These limits reflect the server configuration (CONFIG_BODY_PARSER_LIMIT).
 * 
 * Updated: 2026-01-23
 */

export const FILE_SIZE_LIMITS = {
  image: 16 * 1024 * 1024,       // 16 MB (will be compressed if needed)
  video: 100 * 1024 * 1024,      // 100 MB
  audio: 16 * 1024 * 1024,       // 16 MB
  document: 1024 * 1024 * 1024,  // 1 GB (PDFs, DOCX, etc.)
  sticker: 500 * 1024            // 500 KB
} as const;

/**
 * Get the maximum file size allowed for a given MIME type.
 */
export function getFileSizeLimit(mimeType: string): number {
  if (mimeType.startsWith('image/')) return FILE_SIZE_LIMITS.image;
  if (mimeType.startsWith('video/')) return FILE_SIZE_LIMITS.video;
  if (mimeType.startsWith('audio/')) return FILE_SIZE_LIMITS.audio;
  if (mimeType.includes('sticker')) return FILE_SIZE_LIMITS.sticker;
  return FILE_SIZE_LIMITS.document;
}

/**
 * Get a human-readable label for file type category.
 */
export function getFileTypeLabel(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'imagem';
  if (mimeType.startsWith('video/')) return 'vídeo';
  if (mimeType.startsWith('audio/')) return 'áudio';
  return 'documento';
}

/**
 * Validate file size against the allowed limits.
 * Returns validation result with optional error message.
 */
export function validateFileSize(file: File): { valid: boolean; error?: string } {
  const maxSize = getFileSizeLimit(file.type);
  
  if (file.size > maxSize) {
    const maxFormatted = formatFileSize(maxSize);
    const fileType = getFileTypeLabel(file.type);
    
    return {
      valid: false,
      error: `Arquivo muito grande. Máximo para ${fileType}: ${maxFormatted}`
    };
  }
  
  return { valid: true };
}

/**
 * Format file size in human-readable format.
 */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${bytes} B`;
}
